import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Form, FilterGrid, FilterField, FilterSearchField, formatServiceArea } from '@/components/ui';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import {
  FleetFilterOptions,
  FleetFilters,
  FleetForemanAreaOption,
  FleetScope,
  FleetStatus,
  FLEET_STATUS_LABELS
} from '../../../types/Vehicle';
import { ROAD_TYPES } from '../utils/roadType';

/* ------------------------------------------------------------------------------------------ *
 * Shared Service Area / Foreman Area multi-selects (EQ-14, FLT-04)
 * ------------------------------------------------------------------------------------------ */

const ids = (value?: string): string[] => (value ? value.split(',').map((v) => v.trim()).filter(Boolean) : []);
const joinIds = (values: (string | number)[]): string => values.map(String).join(',');

/** The heading a foreman area is listed under: its service area ("Service Area 16"). */
export function foremanAreaGroup(area: FleetForemanAreaOption): string {
  return formatServiceArea(area.serviceArea ?? null) ?? '';
}

/** "Quality Assurance (SA16)": the name plus its service area, so repeated names can be told apart. */
export function foremanAreaDisplay(area: FleetForemanAreaOption): string {
  const sa = formatServiceArea(area.serviceArea ?? null, true);
  return sa ? `${area.name} (${sa})` : area.name;
}

interface AreaSelectProps {
  options: FleetFilterOptions;
  value?: string;
  onChange: (value: string) => void;
}

export const ServiceAreaMultiSelect: React.FC<AreaSelectProps> = ({ options, value, onChange }) => (
  <MultiSelectDropdown
    options={options.serviceAreas}
    value={ids(value)}
    onChange={(next) => onChange(joinIds(next))}
    getValue={(sa) => sa.id}
    formatOption={(sa) => formatServiceArea(sa) ?? sa.name}
    placeholder="All Service Areas"
    preserveOptionsOrder
    className="tw-text-sm"
  />
);

interface ForemanAreaSelectProps extends AreaSelectProps {
  /** Only list foreman areas in these service areas (comma-separated ids); selected ones stay listed. */
  serviceArea?: string;
}

export const ForemanAreaMultiSelect: React.FC<ForemanAreaSelectProps> = ({ options, value, onChange, serviceArea }) => {
  const selected = ids(value);
  const inServiceAreas = ids(serviceArea);
  const listed = inServiceAreas.length
    ? options.foremanAreas.filter(
        (fa) => inServiceAreas.includes(String(fa.serviceAreaId)) || selected.includes(String(fa.id))
      )
    : options.foremanAreas;
  return (
    <MultiSelectDropdown
      options={listed}
      value={selected}
      onChange={(next) => onChange(joinIds(next))}
      getValue={(fa) => fa.id}
      formatOption={(fa) => fa.name}
      formatDisplay={foremanAreaDisplay}
      groupBy={foremanAreaGroup}
      placeholder="All Foreman Areas"
      className="tw-text-sm"
    />
  );
};

/* ------------------------------------------------------------------------------------------ *
 * Shop board / Dashboard scope: opens on the user's own foreman areas (EQ-14)
 * ------------------------------------------------------------------------------------------ */

/**
 * The Service Area / Foreman Area scope of the Shop board and Dashboard, kept in the URL so Back
 * returns to the same view. With nothing in the URL it is the signed-in user's own foreman areas
 * (`myForemanAreaIds` from the filter options); empty values in the URL mean "all areas".
 * `scope` is null until the options have loaded.
 */
export function useEquipmentScope(options: FleetFilterOptions | null) {
  const [searchParams, setSearchParams] = useSearchParams();
  const explicit = searchParams.has('serviceArea') || searchParams.has('foremanArea');
  const urlServiceArea = searchParams.get('serviceArea') ?? '';
  const urlForemanArea = searchParams.get('foremanArea') ?? '';
  const myAreas = options ? joinIds(options.myForemanAreaIds) : '';

  const defaultScope = useMemo<FleetScope>(() => ({ serviceArea: '', foremanArea: myAreas }), [myAreas]);
  const scope = useMemo<FleetScope | null>(() => {
    if (explicit) return { serviceArea: urlServiceArea, foremanArea: urlForemanArea };
    return options ? defaultScope : null;
  }, [explicit, urlServiceArea, urlForemanArea, options, defaultScope]);

  const setScope = (next: FleetScope) => {
    const params = new URLSearchParams(searchParams);
    params.set('serviceArea', next.serviceArea ?? '');
    params.set('foremanArea', next.foremanArea ?? '');
    setSearchParams(params, { replace: true });
  };

  return { scope, setScope, defaultScope };
}

interface EquipmentScopeFiltersProps {
  options: FleetFilterOptions;
  scope: FleetScope;
  defaultScope: FleetScope;
  onChange: (scope: FleetScope) => void;
  subtitle?: string;
}

const sameScope = (a: FleetScope, b: FleetScope) =>
  (a.serviceArea || '') === (b.serviceArea || '') && (a.foremanArea || '') === (b.foremanArea || '');

/** Service Area + Foreman Area panel for the Shop board and Dashboard. */
export const EquipmentScopeFilters: React.FC<EquipmentScopeFiltersProps> = ({ options, scope, defaultScope, onChange, subtitle }) => {
  const [pending, setPending] = useState<FleetScope>(scope);
  useEffect(() => setPending(scope), [scope]);

  const applied = [scope.serviceArea, scope.foremanArea].filter(Boolean).length;
  const isAll = !scope.serviceArea && !scope.foremanArea;

  return (
    <FiltersCard
      title="Filters"
      subtitle={subtitle}
      collapsible
      defaultCollapsed={false}
      activeFilterCount={applied}
      hasActiveFilters={!sameScope(scope, defaultScope)}
      onApply={() => onChange(pending)}
      applyDisabled={sameScope(pending, scope)}
      onClear={() => onChange(defaultScope)}
    >
      <FilterGrid min="md">
        <FilterField>
          <Form.Label>Service Area</Form.Label>
          <ServiceAreaMultiSelect
            options={options}
            value={pending.serviceArea}
            onChange={(serviceArea) => setPending((prev) => ({ ...prev, serviceArea }))}
          />
        </FilterField>
        <FilterField>
          <Form.Label>Foreman Area</Form.Label>
          <ForemanAreaMultiSelect
            options={options}
            serviceArea={pending.serviceArea}
            value={pending.foremanArea}
            onChange={(foremanArea) => setPending((prev) => ({ ...prev, foremanArea }))}
          />
        </FilterField>
        <FilterField>
          <Form.Label>&nbsp;</Form.Label>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={isAll}
              onClick={() => onChange({ serviceArea: '', foremanArea: '' })}
            >
              Show All Areas
            </Button>
          </div>
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

/* ------------------------------------------------------------------------------------------ *
 * Fleet list panel
 * ------------------------------------------------------------------------------------------ */

interface EquipmentFiltersProps {
  filters: FleetFilters;
  filterOptions: FleetFilterOptions;
  onFilterChange: (filters: FleetFilters) => void;
}

/** Filters this panel shows. Dashboard-only filters appear as chips above the list (FLEET_CHIPS). */
export const PANEL_FIELDS = [
  'search',
  'serviceArea',
  'foremanArea',
  'vehicleClassification',
  'vehicleType',
  'fleetStatus',
  'availability',
  'currentStatus',
  'cvip',
  'overduePm',
  'notVisibleToDrivers',
  'pendingTransfer'
] as const satisfies readonly (keyof FleetFilters)[];

/**
 * Dashboard tile filters without a control of their own: shown as removable chips above the list
 * (LIST-03, EQ-16), e.g. "Showing: Overdue Greasing ✕".
 */
/** The string filters the URL carries (not the sort). */
export type FleetFilterKey = Exclude<keyof FleetFilters, 'sortBy' | 'sortOrder'>;

export const FLEET_CHIPS: { key: FleetFilterKey; label: string }[] = [
  { key: 'recentDefects', label: 'Defects in the Last 24 Hours' },
  { key: 'openDefectsOver30', label: 'Open Defects Over 30 Days' },
  { key: 'overdueGreasing', label: 'Overdue Greasing' },
  { key: 'pastExpectedReturn', label: 'Past Expected Return' },
  { key: 'insuranceExpiring', label: 'Insurance Expiring in 30 Days' },
  { key: 'noInsurance', label: 'No Insurance on File' },
  { key: 'cvipDue', label: 'CVIP Due in 30 Days' },
  { key: 'visibleToDrivers', label: 'Visible to Drivers' }
];

/** Every filter the Fleet list reads from the URL. */
export const FLEET_FILTER_KEYS: FleetFilterKey[] = [...PANEL_FIELDS, ...FLEET_CHIPS.map((chip) => chip.key)];

function pick(filters: FleetFilters): FleetFilters {
  const next: FleetFilters = {};
  PANEL_FIELDS.forEach((key) => {
    next[key] = filters[key] || '';
  });
  return next;
}

/** How many filters hide units: panel fields and chips (FLT-05). */
export function activeFleetFilterCount(filters: FleetFilters): number {
  const panel = PANEL_FIELDS.filter((key) => key !== 'currentStatus' && Boolean(filters[key])).length;
  const availabilityFromStatus = !filters.availability && filters.currentStatus ? 1 : 0;
  return panel + availabilityFromStatus + FLEET_CHIPS.filter((chip) => Boolean(filters[chip.key])).length;
}

const EquipmentFilters: React.FC<EquipmentFiltersProps> = ({ filters, filterOptions, onFilterChange }) => {
  // Pending selections are only sent on Apply, matching the other list screens. Search applies on Enter.
  const [pending, setPending] = useState<FleetFilters>(pick(filters));

  useEffect(() => {
    setPending(pick(filters));
  }, [filters]);

  const applied = pick(filters);
  const activeCount = activeFleetFilterCount(filters);
  const hasUnappliedChanges = PANEL_FIELDS.some((key) => (pending[key] || '') !== (applied[key] || ''));

  const handleChange = (name: keyof FleetFilters, value: string) => {
    setPending((prev) => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    onFilterChange({ ...filters, ...pending, search: (pending.search || '').trim() });
  };

  const handleClearFilters = () => {
    // Clear All returns the list to its defaults: every panel filter and every chip (FLT-03).
    const cleared: FleetFilters = {};
    FLEET_FILTER_KEYS.forEach((key) => {
      cleared[key] = '';
    });
    setPending(pick(cleared));
    onFilterChange({ ...filters, ...cleared });
  };

  return (
    <FiltersCard
      title="Filters"
      subtitle="Filter units by service area, foreman area, classification, type and status"
      collapsible={true}
      defaultCollapsed={false}
      showApplyButton={true}
      onApply={handleApply}
      applyDisabled={!hasUnappliedChanges}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={activeCount > 0}
      activeFilterCount={activeCount}
      search={
        // SRCH-01: in the header bar, visible when the panel is collapsed; runs within the filters.
        <FilterSearchField
          id="equipment-search"
          label="Search units"
          placeholder="Unit #, Description, Make, Model, VIN or GPS Modem"
          value={applied.search || ''}
          onSearch={(search) => onFilterChange({ ...filters, ...pending, search })}
        />
      }
    >
      <FilterGrid min="md">
        <FilterField>
          <Form.Label>Service Area</Form.Label>
          <ServiceAreaMultiSelect
            options={filterOptions}
            value={pending.serviceArea}
            onChange={(value) => handleChange('serviceArea', value)}
          />
        </FilterField>
        <FilterField>
          <Form.Label>Foreman Area</Form.Label>
          <ForemanAreaMultiSelect
            options={filterOptions}
            serviceArea={pending.serviceArea}
            value={pending.foremanArea}
            onChange={(value) => handleChange('foremanArea', value)}
          />
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="equipment-classification">Classification</Form.Label>
          <Form.Select
            id="equipment-classification"
            size="sm"
            value={pending.vehicleClassification || ''}
            onChange={(e) => handleChange('vehicleClassification', e.target.value)}
          >
            <option value="">All Classifications</option>
            {filterOptions.vehicleClassifications.map((classification) => (
              <option key={classification} value={classification}>{classification}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="equipment-type">Type</Form.Label>
          <Form.Select
            id="equipment-type"
            size="sm"
            value={pending.vehicleType || ''}
            onChange={(e) => handleChange('vehicleType', e.target.value)}
          >
            <option value="">All Types</option>
            {ROAD_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label>Equipment Status</Form.Label>
          <MultiSelectDropdown
            options={Object.values(FleetStatus)}
            value={ids(pending.fleetStatus)}
            onChange={(next) => handleChange('fleetStatus', joinIds(next))}
            getValue={(status) => status}
            formatOption={(status) => FLEET_STATUS_LABELS[status]}
            placeholder="All Statuses"
            preserveOptionsOrder
            showSearch={false}
            className="tw-text-sm"
          />
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="equipment-availability">Availability</Form.Label>
          <Form.Select
            id="equipment-availability"
            size="sm"
            value={pending.availability || pending.currentStatus || ''}
            onChange={(e) => {
              const value = e.target.value;
              setPending((prev) => ({
                ...prev,
                availability: value === 'InService' || value === 'OutOfService' ? value : '',
                currentStatus: value === 'In service' || value === 'Out of service' ? value : ''
              }));
            }}
          >
            <option value="">All</option>
            <option value="InService">In Service</option>
            <option value="OutOfService">Out of Service</option>
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label htmlFor="equipment-cvip">CVIP</Form.Label>
          <Form.Select id="equipment-cvip" size="sm" value={pending.cvip || ''} onChange={(e) => handleChange('cvip', e.target.value)}>
            <option value="">Any</option>
            <option value="overdue">Overdue</option>
            <option value="dueSoon">Due in 30 Days</option>
          </Form.Select>
        </FilterField>
        <FilterField>
          <Form.Label>Show Only</Form.Label>
          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-text-content-secondary">
            <label className="tw-mb-0 tw-inline-flex tw-items-center tw-gap-2">
              <input
                type="checkbox"
                className="tw-accent-primary"
                checked={pending.overduePm === '1'}
                onChange={(e) => handleChange('overduePm', e.target.checked ? '1' : '')}
              />
              Overdue PM
            </label>
            <label className="tw-mb-0 tw-inline-flex tw-items-center tw-gap-2">
              <input
                type="checkbox"
                className="tw-accent-primary"
                checked={pending.notVisibleToDrivers === '1'}
                onChange={(e) => handleChange('notVisibleToDrivers', e.target.checked ? '1' : '')}
              />
              Not Visible to Drivers
            </label>
            <label className="tw-mb-0 tw-inline-flex tw-items-center tw-gap-2">
              <input
                type="checkbox"
                className="tw-accent-primary"
                checked={pending.pendingTransfer === '1'}
                onChange={(e) => handleChange('pendingTransfer', e.target.checked ? '1' : '')}
              />
              Pending Transfer
            </label>
          </div>
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default EquipmentFilters;
